import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  setDoc,
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config';

export interface AdminUser {
  id: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'super_admin';
  permissions: string[];
  createdAt: Timestamp;
  lastAccess?: Timestamp;
  isActive: boolean;
}

export class AdminService {
  
  // Check if user is admin
  static async isAdmin(userId: string, email: string): Promise<boolean> {
    try {
      console.log('Checking admin access for user:', userId, email);
      const adminRef = doc(db, 'admins', userId);
      const adminSnap = await getDoc(adminRef);
      if (adminSnap.exists()) {
        const adminData = adminSnap.data() as AdminUser;
        return adminData.isActive && adminData.email === email;
      }
      
      // Also check by email for flexibility
      const emailQuery = query(
        collection(db, 'admins'),
        where('email', '==', email),
        where('isActive', '==', true)
      );
      const emailSnap = await getDocs(emailQuery);
      
      return !emailSnap.empty;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Get admin user data
  static async getAdminUser(userId: string): Promise<AdminUser | null> {
    try {
      const adminRef = doc(db, 'admins', userId);
      const adminSnap = await getDoc(adminRef);
      
      if (adminSnap.exists()) {
        return {
          id: adminSnap.id,
          ...adminSnap.data()
        } as AdminUser;
      }
      return null;
    } catch (error) {
      console.error('Error getting admin user:', error);
      return null;
    }
  }

  // Get all admin users (for super admin)
  static async getAllAdmins(): Promise<AdminUser[]> {
    try {
      const adminsQuery = query(collection(db, 'admins'));
      const adminsSnap = await getDocs(adminsQuery);
      
      return adminsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AdminUser[];
    } catch (error) {
      console.error('Error getting all admins:', error);
      return [];
    }
  }

  // Add new admin (super admin only)
  static async addAdmin(adminData: Omit<AdminUser, 'id' | 'createdAt'>): Promise<void> {
    try {
      const adminRef = doc(collection(db, 'admins'));
      await setDoc(adminRef, {
        ...adminData,
        createdAt: Timestamp.now(),
        lastAccess: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding admin:', error);
      throw error;
    }
  }

  // Update admin access
  static async updateAdminAccess(userId: string, updates: Partial<AdminUser>): Promise<void> {
    try {
      const adminRef = doc(db, 'admins', userId);
      await setDoc(adminRef, {
        ...updates,
        lastAccess: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating admin access:', error);
      throw error;
    }
  }

  // Remove admin access
  static async removeAdmin(userId: string): Promise<void> {
    try {
      const adminRef = doc(db, 'admins', userId);
      await deleteDoc(adminRef);
    } catch (error) {
      console.error('Error removing admin:', error);
      throw error;
    }
  }

  // Log admin access
  static async logAdminAccess(userId: string): Promise<void> {
    try {
      const adminRef = doc(db, 'admins', userId);
      await setDoc(adminRef, {
        lastAccess: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error logging admin access:', error);
    }
  }

  // Check specific permissions
  static async hasPermission(userId: string, permission: string): Promise<boolean> {
    try {
      const adminUser = await this.getAdminUser(userId);
      if (!adminUser || !adminUser.isActive) return false;
      
      return adminUser.permissions.includes(permission) || 
             adminUser.role === 'super_admin';
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }
} 